import { Anchor, Box, Card, CardBody, CardFooter, CardHeader, CheckBox, Text } from 'grommet';
import { DocumentVideo } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { AddNewButton, Delete, EditableText, SelectableText, TalonTip, UsageRow } from '../..';

import { RadioTypes, TVTypes } from '@shared/types';
import {
  Commercial,
  CommercialType,
  Role,
  TCommercialId,
  TUpdateCommercialRequest,
  TUsageId,
  Usage,
  UsageType,
  UsageUnitType,
} from '@talon/web/lib/api';
import {
  useCommercialStore,
  useEstimateStore,
  useProjectStore,
  useRoleStore,
  useUsageStore,
} from '@talon/web/src/context';
import { borderRadiusSize } from 'src/theme';
import { USD } from 'src/utils/currency';
import { getMatchingUsageTypeInfos } from 'src/utils/usages';

export type CommercialUsageRowProps = {
  commercialId: TCommercialId;
  onChecked: () => void;
  checked: boolean;
};

export const CommercialUsageRow = observer(({ commercialId, onChecked, checked }: CommercialUsageRowProps) => {
  /** Context **/
  const estimateStore = useEstimateStore();
  const usageStore = useUsageStore();
  const commercialStore = useCommercialStore();
  const projectStore = useProjectStore();
  const roleStore = useRoleStore();

  /** State **/
  const [commercial, setCommercial] = useState<Commercial>();
  const [selectedUsageIds, setSelectedUsageIds] = useState<TUsageId[]>([]);
  const [roles, setRoles] = useState<Role[]>();
  const [wages, setWages] = useState(0);
  const [total, setTotal] = useState(0);

  /** Computed **/
  const tempEstimate = estimateStore.tempEstimate;
  const usageEstimate = estimateStore.getTempUsageEstimate(commercial?.id);

  /** Methods **/

  const recalculateUsageEstimate = async () => {
    if (!commercial) return;

    const usageEstimate = tempEstimate?.usage_estimates.find(
      (usageEstimate) => usageEstimate.commercial?.id === commercial.id
    );

    if (usageEstimate) await estimateStore.updateUsageEstimate(usageEstimate.id, undefined, true);
  };

  const chooseUsage = (id: TUsageId) => {
    if (selectedUsageIds.includes(id)) {
      setSelectedUsageIds(selectedUsageIds.filter((s) => s !== id));
    } else {
      setSelectedUsageIds([...selectedUsageIds, id]);
    }
  };

  const updateCommercial = async (update: TUpdateCommercialRequest) => {
    if (!commercial) return;
    const updatedCommercial = await commercialStore.updateCommercial(commercial.id, update);

    if (updatedCommercial) {
      setCommercial(updatedCommercial);

      // Refresh the temp estimate
      await recalculateUsageEstimate();
    }
  };

  const addUsage = async (usageType?: UsageType) => {
    if (!commercial) return;

    const usage = await usageStore.createUsage({
      commercialId,
      title: `${usageType} Usage`,
      type: usageType ?? null,
      cycles: 1,
      units: 1,
      unit_type: UsageUnitType.Uses,
      role_ids: [],
      platform: null,
      category: null,
      uses_type: null,
      cycle_number: null,
      cycle_unit: null,
      language: null,
      market_spec: null,
      global_market: null,
      cable_market: null,
      audio_made_move: null,
    });

    if (usage) {
      const usages = [...commercial.usages, usage];
      const updatedCommercial = { ...commercial, usages };

      const usageEstimates = (await commercialStore.getCommercial(commercial.id))?.usage_estimates;
      if (usageEstimates) updatedCommercial.usage_estimates = usageEstimates;

      setCommercial(updatedCommercial);

      // Refresh the temp estimate
      await recalculateUsageEstimate();
    }
  };

  const updateUsage = async (usage: Usage) => {
    if (!commercial) return;

    // Match this Usage to UsageTypes
    const matchingTypeInfos = getMatchingUsageTypeInfos(usage, commercial);

    const {
      id,
      roles,
      units,
      unit_type,
      cycles,
      cycle_number,
      cycle_unit,
      platform,
      category,
      uses_type,
      market_spec,
      language,
      global_market,
      cable_market,
      audio_made_move,
    } = usage;

    let title = 'Untyped Usage';
    let type = null;

    if (usage.type) {
      title = usage.type;
      type = usage.type;
    } else if (matchingTypeInfos.length === 1) {
      title = matchingTypeInfos[0].id;
      type = matchingTypeInfos[0].id;
    }

    const updatedUsage = await usageStore.updateUsage(id, {
      role_ids: roles?.map((r) => r.id),
      title: title,
      type: type,
      units: units,
      unit_type: unit_type,
      cycles: cycles,
      cycle_number: cycle_number,
      cycle_unit: cycle_unit,
      platform: platform,
      category: category,
      uses_type: uses_type,
      market_spec: market_spec,
      language: language,
      global_market: global_market,
      cable_market: cable_market,
      audio_made_move: audio_made_move,
    });
    if (!updatedUsage) return;

    const usages = commercial.usages?.map((u) => (u.id === updatedUsage.id ? updatedUsage : u));
    const updatedCommercial = { ...commercial, usages };
    setCommercial(updatedCommercial);

    // Refresh the temp estimate
    await recalculateUsageEstimate();
  };

  const deleteUsages = async () => {
    if (!commercial || selectedUsageIds.length === 0) return;

    await Promise.all(selectedUsageIds.map((usageId) => usageStore.deleteUsage(usageId)));

    const usages = commercial.usages.filter((usage) => !selectedUsageIds.includes(usage.id));

    setCommercial({ ...commercial, usages });
    setSelectedUsageIds([]);

    // Refresh the temp estimate
    await recalculateUsageEstimate();
  };

  /** Effects **/

  useEffect(() => {
    if (usageEstimate) {
      let wages = 0;
      let total = 0;

      for (const item of usageEstimate.items) {
        wages += item.subtotal;
        total += item.subtotal_plus_fringe;
      }

      setWages(wages);
      setTotal(total);
    }
  }, [usageEstimate]);

  useEffect(() => {
    const getCommercial = async (id: TCommercialId) => {
      if (!projectStore.currentProject) return;
      const c = await commercialStore.getCommercial(id);
      if (c) setCommercial(c);
    };

    getCommercial(commercialId);
  }, [commercialId, projectStore.currentProject]);

  useEffect(() => {
    if (commercial?.type) {
      const getRoles = async (commercialType: CommercialType) => {
        if (!projectStore.currentProject) return;
        const r = await roleStore.getRolesByProjectId(projectStore.currentProject.id);
        setRoles(
          r?.filter((role) =>
            commercialType === CommercialType.Radio
              ? RadioTypes.includes(role.type)
              : commercialType === CommercialType.TV ||
                  commercialType === CommercialType.Digital ||
                  commercialType === CommercialType.Social
                ? TVTypes.includes(role.type)
                : true
          ) || []
        );
      };

      getRoles(commercial.type);
    }
  }, [commercial?.type, projectStore.currentProject]);

  /** Render **/

  return (
    <Card round={borderRadiusSize} background="light-1" animation={{ type: 'fadeIn', duration: 500 }}>
      {/* Header */}
      <CardHeader
        justify="start"
        align="center"
        gap="small"
        background={{ color: 'brand-gradient', dark: true }}
        style={{ background: 'var(--brand-gradient)', zIndex: 1 }}
        height={{ max: '80px', min: '80px' }}
        pad={{ horizontal: 'small' }}
      >
        <Box direction="row" gap="small" pad="small" align="center">
          <CheckBox onChange={onChecked} checked={checked} color="white" />
        </Box>

        <Box direction="row" gap="small" pad="small" align="center">
          <EditableText
            textColor="white"
            val={commercial?.title ?? ''}
            onChange={(title) => updateCommercial({ title })}
          />
        </Box>

        <Box direction="row" gap="small" pad="small" align="center">
          <Text color="white">Type:</Text>
          <SelectableText
            textColor="white"
            options={Object.values(CommercialType).map((t) => ({ label: t, value: t }))}
            val={commercial?.type}
            onChange={(val) => updateCommercial({ type: val as CommercialType })}
          />
        </Box>

        <Box direction="row" gap="small" pad="small" align="center">
          <Text style={{ whiteSpace: 'nowrap' }} color="white">
            Duration:
          </Text>
          <Box direction="row" gap="xxsmall">
            <EditableText
              textColor="white"
              val={commercial?.duration?.toString() ?? ''}
              onChange={(val) => updateCommercial({ durationInSeconds: val ? parseInt(val) : 0 })}
            />
            <Text color="white">seconds</Text>
          </Box>
        </Box>

        <Box flex />

        <Box direction="row" justify="end" pad="small" gap="large">
          <Box direction="row" gap="xsmall" justify="end" align="center" width="">
            <Text size="small" weight={700} color="white">
              WAGES:
            </Text>
            {
              <Text weight="bold" color="white">
                {USD(wages)}
              </Text>
            }
          </Box>
          <Box direction="row" gap="xsmall" justify="end" align="center" width="10rem">
            <Text size="small" weight={700} color="white">
              TOTAL:
            </Text>
            {
              <Text weight="bold" color="white">
                {USD(total)}
              </Text>
            }
          </Box>
        </Box>
      </CardHeader>

      {/* Body */}
      <CardBody background="white" elevation="xsmall">
        {(commercial?.usages ?? []).length > 0 ? (
          commercial?.usages?.map((usage, i) => (
            <UsageRow
              key={usage.id}
              usage={usage}
              commercial={commercial}
              roles={roles ?? []}
              onChecked={() => chooseUsage(usage.id)}
              checked={selectedUsageIds.includes(usage.id)}
              updateUsage={(update) => updateUsage({ ...usage, ...update })}
              rowIndex={i}
            />
          ))
        ) : (
          <Box pad="medium" align="center">
            <Text color="dark-3">
              No Usages found.{' '}
              <Anchor weight={300} onClick={() => addUsage()} color="accent-5">
                Click here to create one
              </Anchor>
              .
            </Text>
          </Box>
        )}
      </CardBody>

      {/* Footer */}
      <CardFooter justify="end" gap="small" background="light-2" pad="small">
        <TalonTip content={<Text>Add Usage Data</Text>}>
          <Box>
            <AddNewButton icon={<DocumentVideo />} disabled={!commercial} onClick={() => commercial && addUsage()} />
          </Box>
        </TalonTip>
        <Delete
          onClick={() => deleteUsages()}
          name="the selected Usages"
          disabled={!selectedUsageIds.length}
          tip="Remove Selected Usages"
        />
      </CardFooter>
    </Card>
  );
});
